import { useState, useMemo, useCallback, useEffect } from 'react';
import { Amplitude3DsecureProperties } from '@packlink/metrics';
import { useTranslation } from '@packlink/translation-provider';
import { PaymentMethod } from '@packlink/packlink-sdk';
import { getPaymentOptionCreditCardStyles, getPaymentOptionCreditCardCardNumberStyles, getPaymentOptionCreditCardCardNumberHiddenStyles, getPaymentOptionCreditCardCardNumberVisibleStyles, getPaymentOptionCreditCardCardDeleteButton } from './PaymentOptionCreditCardStyles.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import { splitAddress, formatText, formatPhone } from '../../../utils/PaymentUtils.js';
import { PaymentStep, LoggerPaymentErrorTagValues, LoggerPaymentContext, LoggerPaymentTag } from '../../../types/types.js';
import { captureException } from '../../../utils/Logger.js';
import { CreditCard } from '../../PaymentComponents/CreditCard/CreditCard.js';
import { CreditCardVault } from '../../PaymentComponents/CreditCardVault/CreditCardVault.js';
import { initDataCollector, initThreeDSecure } from '../../../utils/braintree.js';
import { DeleteVault } from '../../PaymentComponents/DeleteVault/DeleteVault.js';

var BREADCRUMBS = /*#__PURE__*/function (BREADCRUMBS) {
  BREADCRUMBS["SETUP_ERROR"] = "3DSecure setup error";
  BREADCRUMBS["SETUP_SUCCESS"] = "3DSecure setup success";
  BREADCRUMBS["VERIFICATION_ERROR"] = "3DSecure verification error";
  BREADCRUMBS["VERIFICATION_STARTS"] = "3DSecure verification starts";
  BREADCRUMBS["VERIFICATION_SUCCESS"] = "3DSecure verification success";
  return BREADCRUMBS;
}(BREADCRUMBS || {});
const PaymentOptionCreditCard = _ref => {
  let {
    client,
    clientBraintree,
    cdnUrl,
    amount,
    creditCard,
    isPaymentBlocked = false,
    isProcessing,
    sendBillingAddress,
    payButtonText,
    onCheckForExternalError,
    onError,
    onSetupSuccess,
    onSetupError,
    onVerificationStart,
    onVerificationSuccess,
    onVerificationError,
    onDelete
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const [isValidatingBraintree, setIsValidatingBraintree] = useState(false);
  const [threeDSecureInstance, setThreeDSecureInstance] = useState();
  const [dataCollectorInstance, setDataCollectorInstance] = useState();
  const buttonText = payButtonText || t('payment-options.button.credit-card');
  const isButtonLoading = useMemo(() => !isReady || isValidatingBraintree || !!isProcessing, [isReady, isValidatingBraintree, isProcessing]);
  const billingAddress = useMemo(() => {
    if (!client || !sendBillingAddress) {
      return undefined;
    }
    const addressFromChunks = client.address ? splitAddress(client.address) : [];
    return {
      countryCodeAlpha2: client.alpha2Code,
      extendedAddress: addressFromChunks[1] ? addressFromChunks[1] : formatText(client.address2),
      givenName: formatText(client.firstName),
      locality: formatText(client.city),
      phoneNumber: formatPhone(client.phone),
      postalCode: client.zipCode,
      region: formatText(client.location),
      streetAddress: addressFromChunks[0],
      surname: formatText(client.lastName)
    };
  }, [client, sendBillingAddress]);
  const handleVerificationStart = useCallback(() => {
    onVerificationStart(BREADCRUMBS.VERIFICATION_STARTS);
    setIsValidatingBraintree(true);
  }, [onVerificationStart]);
  const handleVerificationSuccess = useCallback(verification => {
    const paymentMetrics = {
      [Amplitude3DsecureProperties.SECURE_CHECK]: verification.threeDSecureInfo.liabilityShiftPossible,
      [Amplitude3DsecureProperties.SECURE_VERIFICATION]: verification.threeDSecureInfo.liabilityShifted,
      [Amplitude3DsecureProperties.SECURE_VERSION]: verification.threeDSecureInfo.threeDSecureVersion,
      [Amplitude3DsecureProperties.CARD]: verification.binData.debit === 'Yes' ? 'Debit' : verification.binData.debit,
      [Amplitude3DsecureProperties.CARD_TYPE]: verification.details.cardType,
      [Amplitude3DsecureProperties.SECURE_STATUS]: verification.threeDSecureInfo.status,
      [Amplitude3DsecureProperties.SECURE_ENROLLED]: verification.threeDSecureInfo.enrolled
    };
    const {
      liabilityShifted,
      liabilityShiftPossible
    } = verification.threeDSecureInfo;
    const hasD3SecureAuthorized = liabilityShifted && liabilityShiftPossible || !liabilityShifted && !liabilityShiftPossible;
    setIsValidatingBraintree(false);
    if (hasD3SecureAuthorized) {
      onVerificationSuccess(PaymentMethod.CREDITCARD3DSECURE, BREADCRUMBS.VERIFICATION_SUCCESS, verification, paymentMetrics);
    } else {
      var _threeDSecureInfo;
      onError === null || onError === void 0 ? void 0 : onError(PaymentStep.VERIFICATION, PaymentMethod.CREDITCARD3DSECURE);
      const context = {
        ...verification,
        nonce: undefined
      };
      const errorMessage =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (context === null || context === void 0 ? void 0 : (_threeDSecureInfo = context.threeDSecureInfo) === null || _threeDSecureInfo === void 0 ? void 0 : _threeDSecureInfo.status) || LoggerPaymentErrorTagValues.PAYMENT_CREDIT_CARD_VERIFICATION_LIABILITY;
      captureException(new Error(errorMessage), {
        contexts: {
          [LoggerPaymentContext.PAYMENT_VERIFICATION]: context
        },
        tags: {
          [LoggerPaymentTag.PAYMENT_ERROR]: LoggerPaymentErrorTagValues.PAYMENT_CREDIT_CARD_VERIFICATION_LIABILITY
        }
      });
    }
  }, [onVerificationSuccess, onError]);
  const handleVerificationError = useCallback(error => {
    onVerificationError(PaymentMethod.CREDITCARD3DSECURE, BREADCRUMBS.VERIFICATION_ERROR, LoggerPaymentErrorTagValues.PAYMENT_CREDIT_CARD_VERIFICATION, error);
    setIsValidatingBraintree(false);
  }, [onVerificationError]);
  const handleDelete = useCallback(() => {
    onDelete(creditCard);
  }, [creditCard, onDelete]);
  const verifyPayment = useCallback(creditCardNonce => {
    if (!creditCardNonce || !creditCardNonce.bin || !creditCardNonce.nonce) {
      handleVerificationError();
    }
    threeDSecureInstance === null || threeDSecureInstance === void 0 ? void 0 : threeDSecureInstance.verifyCard({
      ...creditCardNonce,
      amount,
      challengeRequested: true,
      email: client.email,
      billingAddress
    }).then(data => {
      data.deviceData = dataCollectorInstance ? dataCollectorInstance.deviceData : undefined;
      handleVerificationSuccess(data);
    }).catch(err => {
      if (err.details) {
        err.details.deviceData = dataCollectorInstance ? dataCollectorInstance.deviceData : undefined;
      }
      handleVerificationError(err);
    });
  }, [threeDSecureInstance, amount, client.email, billingAddress, handleVerificationError, dataCollectorInstance, handleVerificationSuccess]);
  useEffect(() => {
    const initializeThreeDSecure = async client => {
      try {
        const dataCollector = await initDataCollector(client);
        const threeDSecure = await initThreeDSecure(client);
        threeDSecure.on('lookup-complete', (_data, next) => {
          next === null || next === void 0 ? void 0 : next();
        });
        setDataCollectorInstance(dataCollector);
        setThreeDSecureInstance(threeDSecure);
        onSetupSuccess(BREADCRUMBS.SETUP_SUCCESS);
        setIsReady(true);
      } catch (error) {
        onSetupError(error, BREADCRUMBS.SETUP_ERROR, LoggerPaymentErrorTagValues.PAYMENT_CREDIT_CARD_SETUP, PaymentMethod.CREDITCARD3DSECURE);
        setIsReady(false);
      }
    };
    clientBraintree && !threeDSecureInstance && initializeThreeDSecure(clientBraintree);
  }, [clientBraintree, onSetupSuccess, onSetupError, threeDSecureInstance]);
  return jsxs(Fragment, {
    children: [!creditCard && jsx(CreditCard, {
      clientBraintree: clientBraintree,
      isLoading: isButtonLoading,
      isPaymentBlocked: isPaymentBlocked,
      cdnUrl: cdnUrl,
      buttonText: buttonText,
      wrapperComponentTag: "section",
      onSetupSuccess: onSetupSuccess,
      onSetupError: onSetupError,
      onCheckForExternalError: onCheckForExternalError,
      onVerificationError: handleVerificationError,
      onVerificationStart: handleVerificationStart,
      onPay: verifyPayment
    }), creditCard && jsxs("section", {
      css: getPaymentOptionCreditCardStyles,
      children: [jsxs("section", {
        css: getPaymentOptionCreditCardCardNumberStyles,
        children: [Array(12).fill(0).map((_v, i) => jsx("span", {
          css: getPaymentOptionCreditCardCardNumberHiddenStyles(Number.isInteger((i + 1) / 4))
        }, i)), jsx("span", {
          css: getPaymentOptionCreditCardCardNumberVisibleStyles,
          children: creditCard.cardNumber
        })]
      }), jsx("section", {
        css: getPaymentOptionCreditCardCardDeleteButton,
        children: jsx(DeleteVault, {
          onDelete: handleDelete
        })
      }), jsx(CreditCardVault, {
        buttonLoading: isButtonLoading,
        buttonText: buttonText,
        creditCard: creditCard,
        isPaymentBlocked: isPaymentBlocked,
        onPay: verifyPayment,
        onCheckForExternalError: onCheckForExternalError,
        onVerificationError: handleVerificationError,
        onVerificationSuccess: handleVerificationSuccess,
        onVerificationStart: handleVerificationStart
      })]
    })]
  });
};

export { PaymentOptionCreditCard };
//# sourceMappingURL=PaymentOptionCreditCard.js.map
